import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { PermissionKey } from '@lms/shared';
import { useAuthStore } from '@/stores/auth';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Optional gate. Absence of a permission renders "not available", never a crash. */
  requires?: PermissionKey[];
  /**
   * Any one of these is enough, for a screen hosting several independently
   * permissioned operations — bulk, where somebody may assign courses in bulk
   * without being allowed to import users.
   */
  requiresAny?: PermissionKey[];
}

/**
 * Route guard. This controls navigation only — every endpoint behind these
 * screens independently enforces the same permission, so bypassing this in the
 * browser gains nothing.
 */
export function ProtectedRoute({ children, requires, requiresAny }: ProtectedRouteProps) {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  const signedOut = useAuthStore((s) => s.signedOut);

  if (isBootstrapping) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
      </div>
    );
  }

  if (!user) {
    /**
     * Remember the destination only when the session ended on its own.
     *
     * A bookmarked link or a session that expired mid-page should return the
     * person to where they were headed. Pressing sign out should not: it left
     * whoever signs in next — often the same person, sometimes not — sitting
     * in the previous session's last screen instead of the dashboard.
     */
    return (
      <Navigate
        to="/login"
        replace
        state={signedOut ? null : { from: `${location.pathname}${location.search}` }}
      />
    );
  }

  // A temporary password blocks everything except the change-password screen.
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  const denied =
    (requires && !requires.every((key) => user.permissions.includes(key))) ||
    (requiresAny && !requiresAny.some((key) => user.permissions.includes(key)));

  if (denied) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
        <h1 className="text-xl font-semibold">You don’t have access to this page</h1>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">
          Your role doesn’t include the permissions this section requires. Contact your
          administrator if you think this is a mistake.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
